import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuotes } from '../hooks/useQuotes';
import { useRequests } from '../../requests/hooks/useRequests';
import { useCustomers } from '../../customers/hooks/useCustomers';
import { useServices } from '../../services/hooks/useServices';
import { Quote, LineItem, QUOTE_STATUS } from '@cleanpro/common-ui';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { calculateQuoteTotals } from '../utils';

/**
 * CreateQuote component provides a form interface for creating a new service quote.
 *
 * - Allows selection of a customer (with search and dropdown).
 * - Optionally initializes from a service request if provided via URL params.
 * - Supports adding, editing, and removing line items (services or custom items).
 * - Calculates subtotal, tax, discount, and total amounts in real-time.
 * - Includes fields for quote settings such as validity date, terms, customer notes, and internal notes.
 * - Handles form submission with validation and quote creation logic.
 * - Navigates back to the quotes list upon successful creation or cancellation.
 *
 * @component
 * @returns {JSX.Element} The rendered CreateQuote form.
 */
export default function CreateQuote() {
  const { addQuote } = useQuotes();
  const { services } = useServices();
  const { requests } = useRequests();
  const { customers } = useCustomers();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('requestId');
  const serviceId = searchParams.get('serviceId');
  const customerId = searchParams.get('customerId');

  const [formData, setFormData] = useState<Quote>({
    id: '',
    requestId: requestId || undefined,
    customerId: customerId || '',
    quoteNumber: '',
    lineItems: [{ 
      id: crypto.randomUUID(), 
      description: '', 
      quantity: 1, 
      unitPrice: 0, 
      total: 0, 
      serviceId: serviceId || undefined 
    }],
    subtotal: 0,
    taxRate: Number.parseFloat(import.meta.env.VITE_DEFAULT_TAX_RATE || '0') || 8.25,
    taxAmount: 0,
    discountAmount: 0,
    totalAmount: 0,
    requiredDeposit: 0,
    status: QUOTE_STATUS.DRAFT,
    validUntil: '',
    terms: 'Payment is due within 30 days of service completion.',
    internalNotes: '',
    customerNotes: '',
    sentAt: '',
    viewedAt: '',
    respondedAt: '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    acceptedAt: '',
    rejectedAt: '',
    cancelledAt: ''
  });

  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize from request if provided
  useEffect(() => {
    const initializeFromRequest = async () => {
    if (requestId) {
      try {
        // Fetch the request directly to avoid context dependency
        const { supabase } = await import('@cleanpro/common-ui');
        const { data, error } = await supabase
          .from('requests')
          .select(`
            *,
            customers!left(id, name, email, phone),
            services!inner(id, name, description, base_price)
          `)
          .eq('id', requestId)
          .single();

        if (error) throw error;

        // Transform and set data
        const service = services.find(s => s.id === data.service_id);
        setFormData(prev => ({
          ...prev,
          requestId: data.id,
          customerId: data.customers?.id || '',
          lineItems: service ? [{
            id: crypto.randomUUID(),
            description: service.name,
            quantity: 1,
            unitPrice: service.basePrice,
            total: service.basePrice,
            serviceId: service.id
          }] : prev.lineItems
        }));
        setCustomerSearch(data.customers?.name || 'Unknown Customer');
      } catch (err) {
        console.error('Error fetching request:', err);
        // setError requires the current state as the first argument
        // You may want to update a specific state here, e.g., setFormData or a store state
        // Example: setError(currentState, 'Failed to load request details');
        // If you don't have a state to update here, you can remove or replace this line with a suitable error handler.
        // For now, just log the error:
        // setError(formData, 'Failed to load request details');
        // Or remove this line if not needed.
      }
    }
  };

  initializeFromRequest();
}, [requestId, services]); // Remove 'requests' dependency since we're fetching directly

  // Set default valid until date (30 days from now)
  useEffect(() => {
    const validUntilDate = new Date();
    validUntilDate.setDate(validUntilDate.getDate() + 30);
    setFormData(prev => ({
      ...prev,
      validUntil: validUntilDate.toISOString().split('T')[0]
    }));
  }, []);

  // Filter customers based on search
  const filteredCustomers = customers;

  /**
   * Finds and returns the customer object from the `customers` array whose `id` matches the `customerId` in `formData`.
   *
   * @remarks
   * This selection is useful for retrieving the full customer details based on the currently selected customer ID in the form.
   *
   * @constant
   * @type {(typeof customers[number] | undefined)}
   *
   * @returns The matched customer object if found, otherwise `undefined`.
   */
  const selectedCustomer = customers.find((c: typeof customers[number]) => c.id === formData.customerId);
  
  /**
   * Finds the selected request object from the `requests` array based on the `requestId` present in `formData`.
   * If `formData.requestId` is not set, returns `null`.
   *
   * @remarks
   * This is typically used to retrieve the full request details for the currently selected request in a form.
   *
   * @const
   * @type {(RequestType | null)}
   * @param formData - The current form data containing the selected request ID.
   * @param requests - The array of available request objects to search.
   * @returns The request object matching the `requestId`, or `null` if not found or not selected.
   */
  const selectedRequest = formData.requestId ? requests.find(r => r.id === formData.requestId) : null;

  // Calculate totals using centralized function for consistency
  const { subtotal, taxAmount, totalAmount } = calculateQuoteTotals(
    formData.lineItems.map(item => ({ ...item, amount: item.total })),
    formData.taxRate,
    formData.discountAmount
  );

  /**
   * Handles the selection of a customer from the customer list.
   * 
   * Finds the customer by the provided `customerId`, updates the form data with the selected customer's ID,
   * sets the customer search input to the selected customer's name, and hides the customer dropdown.
   *
   * @param customerId - The unique identifier of the selected customer.
   */
  const handleCustomerSelect = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setFormData(prev => ({ ...prev, customerId: customer.id || '' }));
      setCustomerSearch(customer.name);
      setShowCustomerDropdown(false);
    }
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, { 
        id: crypto.randomUUID(),
        description: '', 
        quantity: 1, 
        unitPrice: 0, 
        total: 0 
      }]
    }));
  };

  const removeLineItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index)
    }));
  };

  const updateLineItem = (index: number, field: keyof LineItem, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [field]: value };
          // Auto-calculate total when quantity or unitPrice changes
          if (field === 'quantity' || field === 'unitPrice') {
            updatedItem.total = updatedItem.quantity * updatedItem.unitPrice;
          }
          return updatedItem;
        }
        return item;
      })
    }));
  };

  const addServiceAsLineItem = (serviceId: string) => {
    const service = services.find(s => s.id === serviceId);
    if (service) {
      const newLineItem: LineItem = {
        id: crypto.randomUUID(),
        description: service.name,
        quantity: 1,
        unitPrice: service.basePrice,
        total: service.basePrice,
        serviceId: service.id
      };
      setFormData(prev => ({
        ...prev,
        lineItems: [...prev.lineItems, newLineItem]
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.customerId || formData.lineItems.length === 0) {
      alert('Please select a customer and add at least one line item');
      return;
    }

    // Validate line items
    const invalidItems = formData.lineItems.filter(item => !item.description || item.unitPrice <= 0);
    if (invalidItems.length > 0) {
      alert('Please complete all line items with description and valid rates');
      return;
    }

    setIsSubmitting(true);
    try {
      // Generate a quote number
      const quoteNumber = `Q-${Date.now().toString().slice(-6)}`;
      
      // Calculate totals using centralized function for consistency
      const { subtotal, taxAmount, totalAmount } = calculateQuoteTotals(
        formData.lineItems.map(item => ({ ...item, amount: item.total })),
        formData.taxRate,
        formData.discountAmount
      );

      await addQuote({
        requestId: formData.requestId || undefined,
        customerId: formData.customerId,
        quoteNumber,
        lineItems: formData.lineItems,
        subtotal,
        taxRate: formData.taxRate,
        taxAmount,
        discountAmount: formData.discountAmount,
        totalAmount,
        requiredDeposit: formData.requiredDeposit,
        status: QUOTE_STATUS.DRAFT,
        validUntil: formData.validUntil,
        terms: formData.terms,
        internalNotes: formData.internalNotes,
        customerNotes: formData.customerNotes,
        sentAt: '',
        viewedAt: '',
        respondedAt: '',
        acceptedAt: '',
        rejectedAt: '',
        cancelledAt: ''
      });
      navigate('/quotes');
    } catch (error) {
      console.error('Error creating quote:', error);
      alert('Failed to create quote. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center space-x-4 mb-6">
        <button
          onClick={() => navigate('/quotes')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Create Quote</h1>
          <p className="text-gray-600 mt-1">
            {selectedRequest ? `Creating quote for request #${selectedRequest.id.slice(0, 8)}` : 'Create a new service quote'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Request Context (if applicable) */}
        {selectedRequest && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <h3 className="font-semibold text-blue-900 mb-2">Based on Request</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-blue-700 font-medium">Service:</span>
                <div className="text-blue-800">{selectedRequest?.services?.name}</div>
              </div>
              <div>
                <span className="text-blue-700 font-medium">Preferred Date:</span>
                <div className="text-blue-800">{selectedRequest?.requested_date || 'Not specified'}</div>
              </div>
              <div>
                <span className="text-blue-700 font-medium">Urgency:</span>
                <div className="text-blue-800 capitalize">{selectedRequest.urgency}</div>
              </div>
            </div>
            {selectedRequest.notes && (
              <div className="mt-2">
                <span className="text-blue-700 font-medium">Notes:</span>
                <div className="text-blue-800">{selectedRequest.notes}</div>
              </div>
            )}
          </div>
        )}

        {/* Customer Selection */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Customer *
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search customers..."
                  value={customerSearch}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value);
                    setShowCustomerDropdown(true);
                  }}
                  onFocus={() => setShowCustomerDropdown(true)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                  disabled={!!selectedRequest} // Disable if from request
                />
                
                {showCustomerDropdown && !selectedRequest && filteredCustomers.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredCustomers.map(customer => (
                      <button
                        key={customer.id}
                        type="button"
                        onClick={() => handleCustomerSelect(customer.id)}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">{customer.name}</div>
                        <div className="text-sm text-gray-600">{customer.email}</div>
                        <div className="text-sm text-gray-500">{customer.address}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {selectedCustomer && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="font-medium text-blue-900">{selectedCustomer.name}</div>
                  <div className="text-sm text-blue-700">{selectedCustomer.email}</div>
                  <div className="text-sm text-blue-600">{selectedCustomer.phone}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Quote Items</h2>
            <div className="flex space-x-2">
              <select
                onChange={(e) => e.target.value && addServiceAsLineItem(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value=""
              >
                <option value="">Add service</option>
                {services.map(service => (
                  <option key={service.id} value={service.id}>
                    {service.name} - ${service.basePrice}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={addLineItem}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors flex items-center space-x-1"
              >
                <Plus className="h-4 w-4" />
                <span>Add Item</span>
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {formData.lineItems.map((item, index) => (
              <div key={item.id || index} className="grid grid-cols-12 gap-4 items-start p-4 border border-gray-200 rounded-lg">
                <div className="col-span-5">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="Service description"
                    required
                  />
                </div>
                
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Quantity</label>
                  <input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => updateLineItem(index, 'quantity', Number.parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Unit Price</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={item.unitPrice}
                    onChange={(e) => updateLineItem(index, 'unitPrice', Number.parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Total</label>
                  <input
                    type="number"
                    step="0.01"
                    value={item.total}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50"
                  />
                </div>
                
                <div className="col-span-1 flex justify-end">
                  <button
                    type="button"
                    onClick={() => removeLineItem(index)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    disabled={formData.lineItems.length === 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="mt-6 border-t border-gray-200 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tax Rate (%)</label>
                <input
                  type="number"
                  step="0.001"
                  min="0"
                  max="100"
                  value={formData.taxRate}
                  onChange={(e) => setFormData(prev => ({ ...prev, taxRate: Number.parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Discount Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.discountAmount}
                  onChange={(e) => setFormData(prev => ({ ...prev, discountAmount: Number.parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Required Deposit ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.requiredDeposit}
                  onChange={(e) => setFormData(prev => ({ ...prev, requiredDeposit: Number.parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Enter required deposit amount"
                />
              </div>

              <div className="space-y-2 pt-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tax:</span>
                  <span className="font-medium">${taxAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Discount:</span>
                  <span className="font-medium">-${formData.discountAmount.toFixed(2)}</span>
                </div>
                {formData.requiredDeposit > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Required Deposit:</span>
                    <span className="font-medium">${formData.requiredDeposit.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold border-t border-gray-200 pt-2">
                  <span>Total:</span>
                  <span>${totalAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quote Settings */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quote Settings</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Valid Until *
              </label>
              <input
                type="date"
                value={formData.validUntil}
                onChange={(e) => setFormData(prev => ({ ...prev, validUntil: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>

          <div className="mt-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Terms & Conditions
              </label>
              <textarea
                value={formData.terms}
                onChange={(e) => setFormData(prev => ({ ...prev, terms: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Customer Notes
              </label>
              <textarea
                value={formData.customerNotes}
                onChange={(e) => setFormData(prev => ({ ...prev, customerNotes: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={2}
                placeholder="Notes visible to customer"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Internal Notes
              </label>
              <textarea
                value={formData.internalNotes}
                onChange={(e) => setFormData(prev => ({ ...prev, internalNotes: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={2}
                placeholder="Internal notes (not visible to customer)"
              />
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/quotes')}
            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? 'Creating...' : 'Create Quote'}
          </button>
        </div>
      </form>
    </div>
  );
}